import { authenticatedFetch, getValidAccessToken } from "@utils/authenticatedFetch";
import { requireApiOrigin } from "@utils/apiOrigin";

export interface InAppNotification {
  id: number;
  user_id: number;
  facility_id: number | null;
  variant: string;
  title: string;
  body: string;
  facility_name: string | null;
  npi: string | null;
  action_taken: string;
  action_taken_by: number | null;
  reason: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string | null;
}

export interface NotificationsListResponse {
  success: boolean;
  total: number;
  unread_count: number;
  limit: number;
  offset: number;
  notifications: InAppNotification[];
}

export interface MarkReadResponse {
  success: boolean;
  updated: number;
  unread_count: number;
  message?: string;
}

function apiBase(): string {
  return requireApiOrigin();
}

function normalizeNotification(raw: Partial<InAppNotification>): InAppNotification {
  return {
    id: Number(raw.id),
    user_id: Number(raw.user_id ?? 0),
    facility_id: raw.facility_id == null ? null : Number(raw.facility_id),
    variant: String(raw.variant ?? ""),
    title: String(raw.title ?? ""),
    body: String(raw.body ?? ""),
    facility_name: raw.facility_name ?? null,
    npi: raw.npi ?? null,
    action_taken: String(raw.action_taken ?? ""),
    action_taken_by: raw.action_taken_by == null ? null : Number(raw.action_taken_by),
    reason: raw.reason ?? null,
    is_read: Boolean(raw.is_read),
    read_at: raw.read_at ?? null,
    created_at: raw.created_at ?? null,
  };
}

export async function listNotifications(params?: {
  facility_id?: number;
  unread_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<NotificationsListResponse> {
  const query = new URLSearchParams();
  if (params?.facility_id != null) {
    query.set("facility_id", String(params.facility_id));
  }
  if (params?.unread_only) {
    query.set("unread_only", "true");
  }
  query.set("limit", String(params?.limit ?? 50));
  query.set("offset", String(params?.offset ?? 0));
  const url = `${apiBase()}/api/v1/notifications?${query.toString()}`;
  const response = await authenticatedFetch(url);
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Failed to load notifications (${response.status})`);
  }
  const json = (await response.json()) as NotificationsListResponse;
  return {
    success: Boolean(json.success),
    total: Number(json.total ?? 0),
    unread_count: Number(json.unread_count ?? 0),
    limit: Number(json.limit ?? params?.limit ?? 50),
    offset: Number(json.offset ?? params?.offset ?? 0),
    notifications: Array.isArray(json.notifications)
      ? json.notifications.map((n) => normalizeNotification(n))
      : [],
  };
}

export async function getUnreadNotificationCount(): Promise<number> {
  const result = await listNotifications({ limit: 1, offset: 0 });
  return result.unread_count;
}

/** @deprecated Use getUnreadNotificationCount — badge shows unread only. */
export async function getNotificationsTotal(): Promise<number> {
  return getUnreadNotificationCount();
}

export async function markNotificationsRead(opts: {
  notification_ids?: number[];
  mark_all?: boolean;
}): Promise<MarkReadResponse> {
  const url = `${apiBase()}/api/v1/notifications/mark-read`;
  const response = await authenticatedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      notification_ids: opts.notification_ids ?? null,
      mark_all: Boolean(opts.mark_all),
    }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Failed to mark read (${response.status})`);
  }
  const json = (await response.json()) as MarkReadResponse;
  return {
    success: Boolean(json.success),
    updated: Number(json.updated ?? 0),
    unread_count: Number(json.unread_count ?? 0),
    message: json.message,
  };
}

export async function markNotificationRead(id: number): Promise<MarkReadResponse> {
  const url = `${apiBase()}/api/v1/notifications/${id}/read`;
  const response = await authenticatedFetch(url, { method: "POST" });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Failed to mark read (${response.status})`);
  }
  const json = (await response.json()) as MarkReadResponse;
  return {
    success: Boolean(json.success),
    updated: Number(json.updated ?? 0),
    unread_count: Number(json.unread_count ?? 0),
  };
}

const VARIANT_LABELS: Record<string, string> = {
  link_request_sent: "Link request",
  conflict_link_request_sent: "Conflict link request",
  link_request_accepted: "Link accepted",
  link_request_declined: "Link declined",
  unlink_request_sent: "Unlink request",
  unlink_request_approved: "Unlink accepted",
  unlink_request_rejected: "Unlink declined",
};

/** Facility-network in-app variants open My Network (same as email CTA). */
const NETWORK_VARIANTS = new Set([
  "link_request_sent",
  "conflict_link_request_sent",
  "link_request_accepted",
  "link_request_declined",
  "unlink_request_sent",
  "unlink_request_approved",
  "unlink_request_rejected",
]);

export function notificationVariantLabel(variant: string): string {
  return VARIANT_LABELS[variant] || variant.replace(/_/g, " ");
}

export type NotificationPushPayload = {
  id: number;
  user_id: number;
  facility_id: number | null;
  variant: string;
  title: string;
  body?: string;
  is_read: boolean;
  created_at: string | null;
};

type StreamHandlers = {
  onNotification: (payload: NotificationPushPayload) => void;
  onReady?: () => void;
};

/** Tab-wide singleton — StrictMode / remount must not open parallel streams. */
let sharedStreamAbort: AbortController | null = null;
let sharedStreamListeners = new Set<StreamHandlers>();
let sharedReconnectTimer: number | undefined;
let sharedCloseTimer: number | undefined;
let sharedBackoffMs = 5_000;
const SHARED_BACKOFF_MAX_MS = 60_000;
/** Delay close so React StrictMode remount reuses the same fetch (no double /stream). */
const SHARED_CLOSE_GRACE_MS = 400;

function notifySharedReady(): void {
  sharedStreamListeners.forEach((h) => h.onReady?.());
}

function notifySharedNotification(payload: NotificationPushPayload): void {
  sharedStreamListeners.forEach((h) => h.onNotification(payload));
}

function scheduleSharedReconnect(): void {
  if (sharedReconnectTimer != null) return;
  const delay = sharedBackoffMs;
  sharedBackoffMs = Math.min(sharedBackoffMs * 2, SHARED_BACKOFF_MAX_MS);
  sharedReconnectTimer = window.setTimeout(() => {
    sharedReconnectTimer = undefined;
    ensureSharedNotificationStream();
  }, delay);
}

function ensureSharedNotificationStream(): void {
  if (sharedStreamAbort && !sharedStreamAbort.signal.aborted) return;
  if (sharedStreamListeners.size === 0) return;

  const token = getValidAccessToken();
  if (!token) {
    scheduleSharedReconnect();
    return;
  }

  const abort = new AbortController();
  sharedStreamAbort = abort;
  const url = `${apiBase()}/api/v1/notifications/stream`;

  void (async () => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        signal: abort.signal,
      });
      if (!response.ok || !response.body) {
        if (!abort.signal.aborted) scheduleSharedReconnect();
        return;
      }
      sharedBackoffMs = 5_000;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let eventName = "message";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n");
        buffer = parts.pop() ?? "";
        for (const line of parts) {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
            continue;
          }
          if (line.startsWith("data:")) {
            const data = line.slice(5).trim();
            if (eventName === "ready") {
              notifySharedReady();
            } else if (eventName === "notification" && data) {
              try {
                const parsed = JSON.parse(data) as NotificationPushPayload;
                if (parsed?.id != null) notifySharedNotification(parsed);
              } catch {
                // ignore malformed
              }
            }
            eventName = "message";
            continue;
          }
          if (line === "") {
            eventName = "message";
          }
        }
      }
      if (!abort.signal.aborted && sharedStreamListeners.size > 0) {
        scheduleSharedReconnect();
      }
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      if (abort.signal.aborted) return;
      if (sharedStreamListeners.size > 0) scheduleSharedReconnect();
    } finally {
      if (sharedStreamAbort === abort) sharedStreamAbort = null;
    }
  })();
}

/**
 * Subscribe to the tab-wide notification SSE.
 * Returns unsubscribe — closes stream only when last listener leaves
 * (after a short grace so StrictMode remount does not abort+reopen).
 */
export function subscribeNotificationStream(handlers: StreamHandlers): () => void {
  sharedStreamListeners.add(handlers);
  if (sharedCloseTimer != null) {
    window.clearTimeout(sharedCloseTimer);
    sharedCloseTimer = undefined;
  }
  ensureSharedNotificationStream();
  return () => {
    sharedStreamListeners.delete(handlers);
    if (sharedStreamListeners.size > 0) return;
    if (sharedCloseTimer != null) {
      window.clearTimeout(sharedCloseTimer);
    }
    sharedCloseTimer = window.setTimeout(() => {
      sharedCloseTimer = undefined;
      if (sharedStreamListeners.size > 0) return;
      if (sharedReconnectTimer != null) {
        window.clearTimeout(sharedReconnectTimer);
        sharedReconnectTimer = undefined;
      }
      sharedStreamAbort?.abort();
      sharedStreamAbort = null;
      sharedBackoffMs = 5_000;
    }, SHARED_CLOSE_GRACE_MS);
  };
}

export type NotificationNavigateTarget = {
  path: string;
  search?: string;
  state?: Record<string, unknown>;
};

const NETWORK_FOCUS_FACILITY_KEY = "unicomply.network.focusFacilityId";

/** Persist target facility before navigate so Network page can apply even if state drops. */
export function stashNetworkFocusFacilityId(facilityId: number | null | undefined): void {
  if (facilityId == null || !Number.isFinite(Number(facilityId))) return;
  try {
    sessionStorage.setItem(NETWORK_FOCUS_FACILITY_KEY, String(Number(facilityId)));
  } catch {
    // ignore
  }
}

export function consumeNetworkFocusFacilityId(): number | null {
  try {
    const raw = sessionStorage.getItem(NETWORK_FOCUS_FACILITY_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(NETWORK_FOCUS_FACILITY_KEY);
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function peekNetworkFocusFacilityId(): number | null {
  try {
    const raw = sessionStorage.getItem(NETWORK_FOCUS_FACILITY_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Requests-tab deep links (AB#11721 / AB#11728). */
export function notificationOpensRequestsTab(variant: string): boolean {
  return (
    variant === "link_request_sent" ||
    variant === "conflict_link_request_sent" ||
    variant === "unlink_request_sent" ||
    variant === "unlink_request_approved" ||
    variant === "unlink_request_rejected"
  );
}

export const NETWORK_REQUEST_FOCUS_EVENT = "unicomply:network-request-focus";
export const NETWORK_REQUESTS_REFRESH_EVENT = "unicomply:network-requests-refresh";

const REQUEST_FOCUS_STORAGE_KEY = "unicomply.network.requestFocus";

export type NetworkRequestFocus = {
  notificationId: number;
  variant: string;
  facilityName: string | null;
  npi: string | null;
  body: string;
};

function readNetworkRequestFocus(raw: string | null): NetworkRequestFocus | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<NetworkRequestFocus>;
    if (!parsed || typeof parsed.variant !== "string" || !parsed.variant) return null;
    return {
      notificationId: Number(parsed.notificationId ?? 0),
      variant: parsed.variant,
      facilityName: parsed.facilityName ?? null,
      npi: parsed.npi ?? null,
      body: typeof parsed.body === "string" ? parsed.body : "",
    };
  } catch {
    return null;
  }
}

/** Remember which request row a notification click should select. */
export function stashNetworkRequestFocus(
  notification: Pick<InAppNotification, "id" | "variant" | "facility_name" | "npi" | "body">,
): void {
  if (!notificationOpensRequestsTab(notification.variant)) return;
  const focus: NetworkRequestFocus = {
    notificationId: Number(notification.id),
    variant: notification.variant,
    facilityName: notification.facility_name ?? null,
    npi: notification.npi ?? null,
    body: notification.body ?? "",
  };
  try {
    sessionStorage.setItem(REQUEST_FOCUS_STORAGE_KEY, JSON.stringify(focus));
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(NETWORK_REQUEST_FOCUS_EVENT));
}

export function peekNetworkRequestFocus(): NetworkRequestFocus | null {
  try {
    return readNetworkRequestFocus(sessionStorage.getItem(REQUEST_FOCUS_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function consumeNetworkRequestFocus(): void {
  try {
    sessionStorage.removeItem(REQUEST_FOCUS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Live push: Requests tab reloads without waiting for a click. */
export function notifyNetworkRequestsChanged(variant: string): void {
  if (!notificationOpensRequestsTab(variant)) return;
  window.dispatchEvent(new CustomEvent(NETWORK_REQUESTS_REFRESH_EVENT));
}

/** Where click on a notification should go (mark-read happens separately). */
export function notificationNavigateTarget(
  notification: Pick<InAppNotification, "variant" | "facility_id">,
): NotificationNavigateTarget {
  if (NETWORK_VARIANTS.has(notification.variant)) {
    const facilityId =
      notification.facility_id != null ? Number(notification.facility_id) : null;
    const openRequests = notificationOpensRequestsTab(notification.variant);
    const params = new URLSearchParams();
    if (facilityId != null && Number.isFinite(facilityId) && facilityId > 0) {
      params.set("facilityId", String(facilityId));
    }
    if (openRequests) {
      params.set("tab", "requests");
    }
    const search = params.toString();
    return {
      path: "/dashboard/network",
      search: search ? `?${search}` : undefined,
      state: {
        currentView: "facility-network",
        networkTab: openRequests ? "requests" : "overview",
        ...(facilityId != null && Number.isFinite(facilityId) && facilityId > 0
          ? {
              selectedFacilityId: facilityId,
              selectedFacility: {
                facility_id: facilityId,
                id: facilityId,
              },
            }
          : {}),
      },
    };
  }
  return { path: "/dashboard/notifications" };
}
